import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQuery } from '@apollo/client';
import { css } from '@emotion/core';
import { Box } from '@rebass/emotion';

import { isNotNullish } from '@app/helpers/predicate';
import useCart from '@app/shop/hooks/useCart';
import Loader from '@app/components/Loader';
import Card from '@app/components/Card';
import { Button } from '@app/shop/components/Button';
import {
  Magento2_CartItemInput,
  Magento2_ProductStockStatus,
} from '@__generated__/globalTypes';

import FormattedMoney from '../../components/FormattedMoney';
import { TEMP_PRODUCT_BUNDLE_DETAILS_QUERY } from './TempAddProducts.queries';

import { TempProductsQuery_magento2_products_items } from './__generated__/TempProductsQuery';
import {
  TempProductBundleDetailsQuery,
  TempProductBundleDetailsQueryVariables,
  TempProductBundleDetailsQuery_magento2_products_items_Magento2_BundleProduct,
} from './__generated__/TempProductBundleDetailsQuery';

const ItemCard: FC<{
  item: TempProductsQuery_magento2_products_items;
}> = ({ item }) => {
  const { addItems } = useCart();
  const purchaseable = isPurchasable(item);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const isBundleItem = !!(item?.__typename === 'Magento2_BundleProduct');

  const { data: bundleQueryData, loading: bundleDetailsLoading } = useQuery<
    TempProductBundleDetailsQuery,
    TempProductBundleDetailsQueryVariables
  >(TEMP_PRODUCT_BUNDLE_DETAILS_QUERY, {
    skip: !item || !isBundleItem,
    variables: { sku: item.sku ?? '' },
  });

  const handleClick = useCallback(() => {
    if (!item.sku) return;

    const addVars: Magento2_CartItemInput = {
      sku: item.sku,
      quantity: 1,
      ...(isBundleItem ? { selected_options: selectedOptions } : {}),
    };

    addItems([addVars]);
  }, [item, addItems, selectedOptions, isBundleItem]);

  const bundleItem = bundleQueryData?.magento2?.products?.items?.[0] ?? null;

  return (
    <Box
      m={1}
      key={item.uid}
      opacity={purchaseable ? 1 : 0.5}
      data-testid={`product-card-${item.sku}`}
    >
      <Card>
        <strong>{item.name}</strong>
        <br />
        sku: {item.sku}
        <br />
        <ItemPriceRange {...item.priceRange} />
        <small>{item.__typename}</small>
        {purchaseable ? null : (
          <small
            css={css`
              font-weight: bold;
              color: red;
            `}
          >
            {item.stockStatus}
          </small>
        )}
        {bundleItem?.__typename === 'Magento2_BundleProduct' ? (
          <BundleDetails
            isLoading={bundleDetailsLoading}
            items={bundleItem.items}
            selected={selectedOptions}
            setSelected={setSelectedOptions}
          />
        ) : null}
        <Box mt={2}>
          <Button
            type="primary"
            onClick={handleClick}
            data-testid={`add-to-cart-button-${item.sku}`}
            disabled={isBundleItem ? bundleDetailsLoading : false}
            isLoading={isBundleItem ? bundleDetailsLoading : false}
          >
            ADD TO CART
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

export default ItemCard;

const ItemPrice: FC<any> = ({
  finalPrice,
  regularPrice,
  discount,
  fixedProductTaxes,
}) => (
  <Box>
    <strong>
      <FormattedMoney {...finalPrice} />
    </strong>
    <br />
    <small>
      Regular price: <FormattedMoney {...regularPrice} />
    </small>
    <br />
    <small>Discount: {discount?.percentOff ?? 0}%</small>
    <br />
    {fixedProductTaxes?.filter(isNotNullish).map(({ label, amount }) => (
      <small key={label}>
        Tax ({label}): <FormattedMoney {...amount} />
      </small>
    )) ?? null}
  </Box>
);

const ItemPriceRange: FC<
  TempProductsQuery_magento2_products_items['priceRange']
> = ({ minimumPrice, maximumPrice }) => {
  if (
    maximumPrice &&
    minimumPrice.regularPrice.value !== maximumPrice.regularPrice.value
  ) {
    return (
      <>
        <Box mb={1}>
          Minimum Price
          <ItemPrice {...minimumPrice} />
        </Box>
        <Box>
          Maximum Price
          <ItemPrice {...maximumPrice} />
        </Box>
      </>
    );
  }

  return <ItemPrice {...minimumPrice} />;
};

const BundleDetails: FC<{
  items: TempProductBundleDetailsQuery_magento2_products_items_Magento2_BundleProduct['items'];
  selected: string[];
  setSelected: Dispatch<SetStateAction<string[]>>;
  isLoading: boolean;
}> = ({ items, selected, setSelected, isLoading }) => {
  const initialSelect = useMemo(
    () =>
      items
        ?.flatMap((item) => item?.options?.map((opt) => opt?.uid))
        .filter(isNotNullish),
    [items]
  );

  useEffect(() => {
    if (selected.length || !initialSelect?.length) return;
    setSelected(initialSelect);
  }, [selected, setSelected, initialSelect]);

  if (isLoading) return <Loader />;

  return (
    <Box my={2} py={1}>
      {items?.filter(isNotNullish).map((item) => {
        const isSelected = item.options?.some((opt) =>
          selected.includes(opt?.uid ?? '')
        );

        return (
          <Box
            key={item.uid}
            mb={1}
            css={css`
              font-size: 0.96em;
            `}
          >
            <strong>
              {isSelected ? '✓ ' : null}
              {item.title}
            </strong>{' '}
            <small>SKU: {item.sku}</small>
            <ul
              css={css`
                margin: 0;
              `}
            >
              {item.options?.filter(isNotNullish).map((opt) => (
                <li key={opt.uid}>
                  <small>
                    <b>{opt.label}</b> {opt.uid}
                  </small>
                </li>
              ))}
            </ul>
          </Box>
        );
      })}
    </Box>
  );
};

const isPurchasable = (item: TempProductsQuery_magento2_products_items) =>
  !!item &&
  /(virtual|simple|bundle)product/i.test(item.__typename) &&
  item.stockStatus === Magento2_ProductStockStatus.IN_STOCK;
